import React, {
  useContext, useState, useEffect, useRef,
} from 'react';
import {
  ScrollView,
  Keyboard,
  Animated,
  Platform,
} from 'react-native';
import {
  Button, Dialog, Portal, Text, TextInput,
} from 'react-native-paper';
import {
  Grid, LineChart, XAxis, YAxis,
} from 'react-native-svg-charts';
import { useMutation, useQuery } from '@apollo/client';
import { scaleTime } from 'd3-scale';
import {
  Circle, Defs, G, LinearGradient, Rect, Stop, Text as SvgText,
} from 'react-native-svg';
import { Formik } from 'formik';
import {
  FormControl, NativeBaseProvider, View,
} from 'native-base';
import {
  startOfWeek, parseISO, format,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { CreateWeightMutation } from '../../gql/profile/mutation';
import { weightSchema } from '../../utils/yupSchema';
import { FindMyWeightsOnProfile, FindWeightsOnOneProfile } from '../../gql/profile/query';
import { styles } from '../../screens/Common.style';
import { ModalContext } from '../../context/ModalContext';
import { FindOneUserQuery } from '../../gql/user/query';
import LoadingIndicator from '../../components/commons/LoadingIndicator';

const axesSvg = { fontSize: 10, fill: 'grey' };
const verticalContentInset = { top: 10, bottom: 10 };
const xAxisHeight = 60;

const processWeightsForChart = (weights) => {
  if (!weights || weights.length === 0) {
    return [];
  }

  const weeklyWeights = weights.reduce((acc, weight) => {
    const weekStartDate = startOfWeek(parseISO(weight.createdAt), { weekStartsOn: 1 }); // Assuming week starts on Monday
    const weekKey = weekStartDate.toISOString();

    if (!acc[weekKey]) {
      acc[weekKey] = {
        weights: [],
        total: 0,
      };
    }

    acc[weekKey].weights.push(weight.value);
    acc[weekKey].total += weight.value;

    return acc;
  }, {});

  return Object.entries(weeklyWeights).map(([weekKey, data]) => ({
    createdAt: weekKey,
    value: data.total / data.weights.length,
  })).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

function Gradient() {
  return (
    <Defs key="gradient">
      <LinearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <Stop offset="0%" stopColor="rgb(134, 65, 244)" stopOpacity={0.8} />
        <Stop offset="100%" stopColor="rgb(134, 65, 244)" stopOpacity={0.2} />
      </LinearGradient>
    </Defs>
  );
}

function WeightTracker() {
  const { displayWeightModal, toggleWeightModal } = useContext(ModalContext);
  const [createWeightForProfile] = useMutation(CreateWeightMutation);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const shift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return () => { };
    }
    const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (e) => {
      Animated.timing(shift, {
        toValue: -(e.endCoordinates.height / 2),
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    const keyboardWillHide = Keyboard.addListener('keyboardWillHide', (e) => {
      Animated.timing(shift, {
        toValue: 0,
        duration: e.duration,
        useNativeDriver: true,
      }).start();
    });

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, [shift]);


  const { data, loading } = useQuery(
    FindWeightsOnOneProfile,
    {
      variables: { profileId: Number(displayWeightModal?.profileId) },
      skip: !displayWeightModal?.profileId,
    },
  );
  const { data: myData, loading: myLoading } = useQuery(
    FindMyWeightsOnProfile,
    {
      skip: displayWeightModal?.profileId !== undefined,
    },
  );

  const profile = displayWeightModal?.profileId ? data?.findOneProfile : myData?.findMyProfile;
  const chartData = processWeightsForChart(profile?.weights);

  const Decorator = ({ x, y, data: decoratorData }) => decoratorData.map((item) => {
    const xPos = x(new Date(item.createdAt).valueOf());
    const yPos = y(item.value);

    return (
      <G key={item.createdAt}>
        <Circle
          cx={xPos}
          cy={yPos}
          r={8}
          fill="rgba(134, 65, 244, 0.2)"
          onPress={() => setSelectedPoint(selectedPoint?.createdAt === item.createdAt ? null : item)}
        />
        <Circle
          cx={xPos}
          cy={yPos}
          r={4}
          fill="rgb(134, 65, 244)"
          onPress={() => setSelectedPoint(selectedPoint?.createdAt === item.createdAt ? null : item)}
        />
        {selectedPoint?.createdAt === item.createdAt && (
          <G
            x={xPos - 20}
            y={yPos - 40}
          >
            <Rect
              height="30"
              width="40"
              stroke="grey"
              fill="white"
              ry="10"
              rx="10"
            />
            <SvgText
              x="20"
              y="20"
              textAnchor="middle"
              fontWeight="bold"
              fontSize="12"
            >
              {`${item.value.toFixed(1)}kg`}
            </SvgText>
          </G>
        )}
      </G>
    );
  });

  return loading || myLoading ? (
    <LoadingIndicator text="Chargement des données..." />
  ) : profile && (
    <Formik
      initialValues={{
        weight: profile.weights?.length > 0
          ? profile.weights[profile.weights?.length - 1].value
          : 0,
      }}
      validateOnChange
      validationSchema={weightSchema}
      onSubmit={async (values) => {
        await createWeightForProfile({
          variables: {
            value: Number.parseFloat(values.weight),
            profileId: profile.id,
          },
          refetchQueries: [
            FindOneUserQuery, // DocumentNode object parsed with gql
            'findOneUser', // Query name
          ],
        });
        toggleWeightModal();
      }}
    >
      {({
        handleSubmit, values, errors, setFieldValue,
      }) => (
        <Animated.View style={[{ flex: 1, transform: [{ translateY: shift }] }]}>
          <Dialog visible={displayWeightModal.display} dismissable onDismiss={toggleWeightModal}>
            <ScrollView>
              <Dialog.Title>Suivi du poids</Dialog.Title>
              <Dialog.Content>
                {chartData.length > 0 ? (
                  <View style={{ height: 200, flexDirection: 'row' }}>
                    <YAxis
                      data={chartData}
                      yAccessor={({ item }) => item.value}
                      style={{ marginBottom: xAxisHeight, width: 40 }}
                      contentInset={verticalContentInset}
                      numberOfTicks={6}
                      svg={axesSvg}
                      formatLabel={(value) => `${value.toFixed(1)}`}
                    />
                    <View style={{ flex: 1, marginLeft: 10 }}>
                      <LineChart
                        style={{ flex: 1 }}
                        data={chartData}
                        xAccessor={({ item }) => new Date(item.createdAt).valueOf()}
                        yAccessor={({ item }) => item.value}
                        scale={scaleTime}
                        contentInset={verticalContentInset}
                        svg={{ stroke: 'url(#gradient)' }}
                      >
                        <Grid />
                        <Gradient />
                        <Decorator />
                      </LineChart>
                      <XAxis
                        style={{ height: xAxisHeight }}
                        data={chartData}
                        scale={scaleTime}
                        xAccessor={({ item }) => new Date(item.createdAt).valueOf()}
                        formatLabel={(value) => format(new Date(value), 'd MMM', { locale: fr })}
                        numberOfTicks={4}
                        contentInset={{ left: 20, right: 20 }}
                        svg={{
                          fontSize: 10,
                          fill: 'grey',
                          rotation: -45,
                          originY: 10,
                          y: 10,
                        }}
                      />
                    </View>
                  </View>
                ) : null}
                <FormControl style={[styles.field]}>
                  <TextInput
                    label="Poids"
                    onChangeText={(text) => {
                      const numericValue = parseFloat(text);
                      if (!Number.isNaN(numericValue)) {
                        setFieldValue('weight', numericValue);
                      } else if (text === '') {
                        setFieldValue('weight', '');
                      }
                    }}
                    value={values.weight.toString()}
                    keyboardType="numeric"
                    left={<TextInput.Icon icon="weight" />}
                    right={<TextInput.Affix text="kg" />}
                  />
                </FormControl>
                {!!errors.weight && <Text style={styles.error}>{errors.weight}</Text>}
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={handleSubmit}>
                  Envoyer
                </Button>
              </Dialog.Actions>
            </ScrollView>
          </Dialog>
        </Animated.View>
      )}
    </Formik>
  );
}

export default function WeightTrackerModal() {
  return (
    <Portal>
      <NativeBaseProvider>
        <WeightTracker />
      </NativeBaseProvider>
    </Portal>
  );
}